/*
 * sh-variant-aware-card-links.js
 * SH Updated: May 2026 v2 rebuild
 *
 * Hides cards on filtered collection grids when the card's bound variant
 * is sold out (and any filter is active), or when the HQ-availability
 * metafield filter is active and the variant doesn't match it.
 *
 * Cooperates with — does not replace — the B4B Variants Products app.
 *   B4B:        renders one card per colour, filters by size / colour /
 *               price / native-availability at Liquid time. Each card
 *               carries `data-variant-id` for the specific variant it
 *               represents.
 *   This file:  closes the two gaps B4B leaves open —
 *                 1. inventory.hq_available metafield filter (B4B's
 *                    namespace allow-list does not include `inventory`)
 *                 2. unavailable variants leaking into filtered grids
 *                    (B4B only enforces availability when the customer
 *                    explicitly toggles filter.v.availability, which
 *                    Goldtop does not expose in Search & Discovery)
 *
 * Data source: <script data-sh-catalog> emitted by
 * snippets/collection-grid.liquid. Shape:
 *   [{ id: <number>, available: <bool>, hq: <bool|null> }, ...]
 *
 * Trigger model (initial-load timing matters — B4B injects cards into the
 * DOM AFTER DOMContentLoaded, so we need to re-run when they appear):
 *   1. DOMContentLoaded — first attempt for any server-rendered cards
 *   2. window.load — second attempt (catches B4B's late-init injection)
 *   3. collection:reloaded — Impulse's AJAX filter swap event
 *   4. MutationObserver on the grid wrapper — catches every dynamic card
 *      insertion regardless of source. This is the load-bearing trigger;
 *      the first three are belt-and-suspenders.
 *
 * Strategy: parse URL filters → look up each visible card's
 * data-variant-id in the catalog → hide via display:none when the
 * variant fails an active filter axis. Strict mode (Joe's stated
 * preference): "show only the variants that are available and in stock."
 */

(function () {
  // Debug mode — gated. Activate any of:
  //   - URL: ?sh_debug=1
  //   - Console: window.SH_DEBUG = true; then re-run window.shDebug()
  //   - Persistent: localStorage.setItem('sh_debug', '1')
  // Strip this block + dlog() calls + window.shDebug helper before
  // delivery to Joe (mirror the cart-rules debug-strip pattern).
  var DEBUG = (function () {
    try {
      if (window.SH_DEBUG === true) return true;
      if (new URLSearchParams(window.location.search).get('sh_debug') === '1') return true;
      if (localStorage.getItem('sh_debug') === '1') return true;
    } catch (e) {}
    return false;
  })();
  function dlog() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[SH-P1]');
    console.log.apply(console, args);
  }
  dlog('Script loaded — DEBUG mode on. Call window.shDebug() to re-run and inspect.');

  function parseFilters() {
    var params = new URLSearchParams(window.location.search);

    var hqRaw = params.get('filter.v.m.inventory.hq_available');
    var hq = null;
    if (hqRaw === '1' || hqRaw === 'true') hq = true;
    else if (hqRaw === '0' || hqRaw === 'false') hq = false;

    var anyFilter = false;
    var keys = params.keys();
    var entry = keys.next();
    while (!entry.done) {
      if (entry.value.indexOf('filter.') === 0) {
        anyFilter = true;
        break;
      }
      entry = keys.next();
    }

    return { hq: hq, anyFilter: anyFilter };
  }

  // hq_available may arrive as primitive (true / "true" / 1) or a
  // Metafield drop object ({value, type, ...}) depending on Shopify
  // version. Treat null / undefined / "" as "not set" — not true.
  function hqIsTrue(value) {
    if (value && typeof value === 'object' && 'value' in value) return hqIsTrue(value.value);
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  function loadCatalog() {
    var el = document.querySelector('script[data-sh-catalog]');
    if (!el) return null;
    var rows;
    try { rows = JSON.parse(el.textContent); } catch (e) { return null; }
    if (!Array.isArray(rows)) return null;
    var map = Object.create(null);
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      if (r && r.id != null) map[String(r.id)] = r;
    }
    return map;
  }

  function shouldHideCard(variantData, filters) {
    // Unknown variant — leave visible. Catalog may be missing the
    // variant in rare cases (race with Section Rendering API swap);
    // we never want to disappear a card we can't reason about.
    if (!variantData) return false;

    // No filter active → never hide anything (default browse view).
    if (!filters.anyFilter) return false;

    // Strict mode: any filter implies "I only want to see things I
    // can actually buy." Sold-out variants are hidden.
    if (variantData.available !== true) return true;

    // HQ-availability metafield filter — the gap B4B leaves open.
    if (filters.hq === true && !hqIsTrue(variantData.hq)) return true;
    if (filters.hq === false && hqIsTrue(variantData.hq)) return true;

    return false;
  }

  function findCards() {
    // B4B cards carry data-variant-id and class `variable-products`.
    // Standard Impulse fallback cards (if B4B is ever disabled per
    // product) also use data-variant-id. Use the attribute as the
    // universal selector so we don't depend on B4B-specific classes.
    return document.querySelectorAll('[data-variant-id]');
  }

  function updateCountDisplay(visibleCount) {
    var el = document.querySelector('[data-sh-count-display]')
          || document.querySelector('.collection-filter__item--count');
    if (!el) return;
    var current = (el.textContent || '').trim();
    // Replace the leading number; preserves locale suffix ("15 products").
    var next = current.replace(/^\s*\d+/, String(visibleCount));
    if (next !== current) el.textContent = next;
  }

  function run() {
    var filters = parseFilters();
    var catalog = loadCatalog();
    dlog('run() — filters:', filters, '| catalog:', catalog ? Object.keys(catalog).length + ' variants' : 'MISSING (script[data-sh-catalog] absent or unparseable)');
    if (!catalog) return;

    var cards = findCards();
    dlog('Cards on page:', cards.length);
    if (!cards.length) return;

    var hidden = 0;
    var debugRows = DEBUG ? [] : null;
    var debugWarnings = DEBUG ? [] : null;
    var skippedNoVid = 0;
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var vid = card.getAttribute('data-variant-id');
      if (!vid) { skippedNoVid++; continue; }
      var data = catalog[String(vid)];
      var hide = shouldHideCard(data, filters);
      if (hide) {
        card.style.display = 'none';
        card.setAttribute('data-sh-hidden', '1');
        hidden++;
      } else if (card.getAttribute('data-sh-hidden') === '1') {
        card.style.display = '';
        card.removeAttribute('data-sh-hidden');
      }
      if (debugRows) {
        var anchor = card.querySelector('a[href]');
        var href = anchor ? anchor.getAttribute('href') : '(no anchor)';
        var hrefVidMatch = (href || '').match(/[?&]variant=(\d+)/);
        var hrefVid = hrefVidMatch ? hrefVidMatch[1] : null;
        var hrefData = hrefVid ? catalog[String(hrefVid)] : null;
        var titleEl = card.querySelector('.grid-product__title')
                   || card.querySelector('[class*="product__title"]')
                   || card.querySelector('a[href*="/products/"]');
        var cardTitle = titleEl ? (titleEl.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60) : '';
        var hqVal = data ? (data.hq && typeof data.hq === 'object' ? data.hq.value : data.hq) : null;
        var hrefHqVal = hrefData ? (hrefData.hq && typeof hrefData.hq === 'object' ? hrefData.hq.value : hrefData.hq) : null;
        debugRows.push({
          idx: i,
          card_title: cardTitle,
          card_vid: vid,
          in_catalog: !!data,
          available: data ? data.available : null,
          hq: hqVal,
          decision: hide ? 'HIDE' : 'SHOW',
          href_vid: hrefVid,
          vid_mismatch: hrefVid && hrefVid !== vid ? '⚠️' : '',
          href_in_catalog: hrefVid ? !!hrefData : null,
          href_available: hrefData ? hrefData.available : null,
          href_hq: hrefHqVal,
        });

        // Loud warning when the script and the click would look at DIFFERENT
        // variants — the most likely shape of "card visible but PDP loads OOS"
        if (!hide && hrefVid && hrefVid !== vid) {
          var hrefDesc = hrefData
            ? ('available=' + hrefData.available + ', hq=' + hrefHqVal)
            : 'NOT IN CATALOG';
          debugWarnings.push(
            'Card "' + (cardTitle || vid) + '"\n' +
            '  Script checked card_vid=' + vid + ' → available=' + (data ? data.available : '?') + ', hq=' + hqVal + ' → ' + (hide ? 'HIDE' : 'SHOW') + '\n' +
            '  Click goes to  href_vid=' + hrefVid + ' → ' + hrefDesc
          );
        }
      }
    }

    if (DEBUG) {
      var visible = cards.length - hidden - skippedNoVid;
      console.groupCollapsed('[SH-P1] Decisions — ' + cards.length + ' cards | ' + visible + ' shown | ' + hidden + ' hidden | ' + skippedNoVid + ' skipped (no vid)');
      console.table(debugRows);
      console.groupEnd();
      if (debugWarnings.length) {
        console.group('[SH-P1] ⚠️ ' + debugWarnings.length + ' card(s) with vid_mismatch — script/click see different variants');
        for (var w = 0; w < debugWarnings.length; w++) console.warn(debugWarnings[w]);
        console.groupEnd();
      } else {
        dlog('No vid_mismatch warnings.');
      }
      window.__SH_LAST_DEBUG = { filters: filters, rows: debugRows, hidden: hidden, visible: visible, skippedNoVid: skippedNoVid, warnings: debugWarnings };
    }

    if (filters.anyFilter) {
      updateCountDisplay(cards.length - hidden);
    }
  }

  // Debounce — when B4B injects 25 cards via 25 separate DOM operations,
  // the MutationObserver would otherwise fire 25 times. Coalesce.
  var runScheduled = false;
  function scheduleRun() {
    if (runScheduled) return;
    runScheduled = true;
    setTimeout(function () {
      runScheduled = false;
      run();
    }, 30);
  }

  function setupObserver() {
    if (typeof MutationObserver === 'undefined') return;
    if (!document.body) return;

    // Observe document.body (not the grid wrapper). Impulse's AJAX
    // filter swap can REPLACE the wrapper element wholesale via the
    // Section Rendering API — observing the wrapper directly leaves
    // us with a stale handle to a detached node and we never see the
    // new cards. Body is stable across all section swaps; the early
    // exit on no-card-insertion keeps the work cheap.
    var observer = new MutationObserver(function (mutations) {
      var sawCardInsert = false;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.hasAttribute && node.hasAttribute('data-variant-id')) {
            sawCardInsert = true;
            break;
          }
          if (node.querySelector && node.querySelector('[data-variant-id]')) {
            sawCardInsert = true;
            break;
          }
        }
        if (sawCardInsert) break;
      }
      if (sawCardInsert) {
        dlog('Trigger: MutationObserver — card insertion detected, scheduling run');
        scheduleRun();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Initial multi-trigger boot — belt and suspenders.
  function boot() {
    run();
    setupObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // window.load fires after all sync resources have loaded — catches
  // anything injected during script init that happens between
  // DOMContentLoaded and load.
  window.addEventListener('load', function () { dlog('Trigger: window.load'); run(); });

  // Impulse's documented filter-reload event.
  document.addEventListener('collection:reloaded', function () { dlog('Trigger: collection:reloaded'); run(); });

  // Shopify's standard Section Rendering API event — fires whenever any
  // section is re-rendered (including filter-driven swaps). Belt and
  // suspenders for the case where Impulse's collection:reloaded doesn't
  // fire or fires before B4B has re-injected cards.
  document.addEventListener('shopify:section:load', function () { dlog('Trigger: shopify:section:load'); run(); });

  // Debug helpers — gated on DEBUG. window.shDebug() re-runs and returns
  // last result; window.__SH_LAST_DEBUG holds it. shDebugFindByTitle(q)
  // returns rows whose card_title matches the query (case-insensitive).
  if (DEBUG) {
    window.shDebug = function () {
      run();
      return window.__SH_LAST_DEBUG;
    };
    window.shDebugFindByTitle = function (query) {
      var q = (query || '').toLowerCase();
      var rows = (window.__SH_LAST_DEBUG && window.__SH_LAST_DEBUG.rows) || [];
      return rows.filter(function (r) {
        return (r.card_title || '').toLowerCase().indexOf(q) !== -1;
      });
    };
  }
})();
